import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { 
    useGetAdsPlaCompetitiveIntelligenceQuery,
    useGetTextAdsQuery,
    useGetAdsRightPanelGlobalQuery,
    useGetSideQueriesQuery,
} from 'src/context/api/competitiveIntelligence/api';
import { fDate } from 'src/utils/format-time';
import { PlaItem, TextAdItem, DomainItem } from '../types';
import { DEFAULT_API_PARAMS } from '../utils';

export const useAdsData = (selectedCountry: string | null) => {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;
    const skipAdsQuery = skipQuery || !selectedCountry;
    const currentCountryFilter = selectedCountry ? [selectedCountry] : [];

    // Clamp side queries date range to a max window to avoid heavy scans (performance)
    const sideMaxDays = 60; // limit to last 60 days (overrideable)
    const rawStart = filterStore.start ? new Date(filterStore.start) : null;
    const rawEnd = filterStore.end ? new Date(filterStore.end) : null;
    let sideStartISO: string | null = rawStart ? fDate(rawStart, "yyyy-MM-dd") : null;
    let sideEndISO: string | null = rawEnd ? fDate(rawEnd, "yyyy-MM-dd") : null;
    const fullRangeOverride = (typeof window !== 'undefined') && (localStorage.getItem('ci.sideQueries.fullRange') === '1');
    if (rawStart && rawEnd) {
        const msPerDay = 24 * 60 * 60 * 1000;
        const diffDays = Math.floor((rawEnd.getTime() - rawStart.getTime()) / msPerDay);
        if (!fullRangeOverride && diffDays > sideMaxDays) {
            const clampedStart = new Date(rawEnd.getTime() - sideMaxDays * msPerDay);
            sideStartISO = fDate(clampedStart, "yyyy-MM-dd");
            sideEndISO = fDate(rawEnd, "yyyy-MM-dd");
        }
    }

    // Toutes les requêtes exécutées en parallèle pour optimiser les performances
    const [
        { data: sideQueriesData, isFetching: isFetchingSide },
        { data: plaAdsData, isFetching: isFetchingPLA },
        { data: textAdsData, isFetching: isFetchingTxt },
        { data: rightPanelAdsData, isFetching: isFetchingRightPanel }
    ] = [
        useGetSideQueriesQuery({
            startDate: sideStartISO,
            endDate: sideEndISO,
            device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
            countries: currentCountryFilter,
            keywords: filterStore?.keywords || [],
            workspaceTable: workspace?.bqTable,
            workspaceId: workspace?.id,
            platform: filterStore?.platforms || [],
            // adType: [] // global view; if needed we can pass ['PLA'] or ['TEXT']
        }, { skip: skipAdsQuery, refetchOnFocus: false, refetchOnReconnect: false }),
        
        useGetAdsPlaCompetitiveIntelligenceQuery({
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
            device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
            countries: currentCountryFilter,
            keywords: filterStore?.keywords || [],
            workspaceTable: workspace?.bqTable,
            workspaceId: workspace?.id,
            domain: "all",
            platform: filterStore?.platforms || []
        }, { skip: skipAdsQuery }),
        
        useGetTextAdsQuery({
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
            domain: "all",
            device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
            countries: currentCountryFilter,
            keywords: filterStore.keywords || [],
            workspaceTable: workspace?.bqTable,
            workspaceId: workspace?.id,
            platform: filterStore?.platforms || []
        }, { skip: skipAdsQuery }),
        
        useGetAdsRightPanelGlobalQuery({
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
            domain: "all",
            device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
            countries: currentCountryFilter,
            keywords: filterStore.keywords || [],
            workspaceTable: workspace?.bqTable,
            workspaceId: workspace?.id,
            platform: filterStore?.platforms || [],
            // Defaults compatible avec le backend CI
            impressionShare: [0, 100],
            position: [0, 10],
        }, { skip: skipAdsQuery })
    ];

    // Process PLA ads data
    const plaItems: PlaItem[] = useMemo(() => {
        if (!plaAdsData?.ads) return [];
        return plaAdsData.ads.map((ad: any) => {
            const avgPosition = typeof ad.avg_item_position === 'number' ? ad.avg_item_position : (typeof ad.position === 'number' ? ad.position : 0);
            const freqRaw = ad.frequency != null ? ad.frequency : (ad.impression_share != null ? ad.impression_share * 100 : 0);
            return {
                title: ad.title || '',
                price: ad.price_label || ad.price || '',
                img: ad.image || undefined,
                searchTerm: ad.search_term || '',
                position: Number((avgPosition || 0).toFixed(0)),
                frequency: Math.round(freqRaw || 0),
                domain: ad.domain_name || ad.domain || '',
                _gcs_uri_desktop: ad._gcs_uri_desktop || null,
                _gcs_uri_mobile: ad._gcs_uri_mobile || null,
                device_type: ad.device_type || null,
                brandInfringementTitle: Boolean(ad.brand_infringement_title),
                brandInfringementDescription: Boolean(ad.brand_infringement_description),
            };
        });
    }, [plaAdsData]);

    // Process text ads data  
    const textAds: TextAdItem[] = useMemo(() => {
        if (!textAdsData?.ads) return [];
        return textAdsData.ads.map((ad: any) => {
            const avgPosition = typeof ad.avg_item_position === 'number' ? ad.avg_item_position : (typeof ad.position === 'number' ? ad.position : 0);
            const freqRaw = ad.frequency != null ? ad.frequency : (ad.impression_share != null ? ad.impression_share * 100 : 0);
            return {
                domain: ad.domain_name || ad.domain || '',
                title: ad.title || '',
                description: ad.description || '',
                position: Number((avgPosition || 0).toFixed(0)),
                frequency: Math.round(freqRaw || 0),
                searchTerm: ad.search_term || '',
                _gcs_uri_desktop: ad._gcs_uri_desktop || null,
                _gcs_uri_mobile: ad._gcs_uri_mobile || null,
                device_type: ad.device_type || null,
                brandInfringementTitle: Boolean(ad.brand_infringement_title),
                brandInfringementDescription: Boolean(ad.brand_infringement_description),
            };
        });
    }, [textAdsData]);


    // Process right panel ads data
    const rightPanelItems: PlaItem[] = useMemo(() => {
        const rows = Array.isArray(rightPanelAdsData)
            ? rightPanelAdsData
            : (rightPanelAdsData?.ads ?? []);
        if (!rows || rows.length === 0) return [];

        // Conserver les entrées même si certains champs (search_term, price) manquent
        return rows
            .filter((ad: any) => ad?.title && ad?.domain_name)
            .map((ad: any) => ({
                title: ad.title || '',
                price: ad.price_label || ad.price || '',
                img: ad.image || undefined,
                searchTerm: ad.search_term || '',
                position: Number(((ad.avg_item_position ?? 0)).toFixed(0)),
                frequency: Math.round(ad.frequency ?? 0),
                domain: ad.domain_name || '',
                _gcs_uri_desktop: ad._gcs_uri_desktop || null,
                _gcs_uri_mobile: ad._gcs_uri_mobile || null,
                device_type: ad.device_type || null,
                brandInfringementTitle: Boolean(ad.brand_infringement_title),
                brandInfringementDescription: Boolean(ad.brand_infringement_description),
            }));
    }, [rightPanelAdsData]);


    // Map side queries top_competitors to DomainItem (value = paid_presence_rate)
    const domainItems: DomainItem[] = useMemo(() => {
        const rows = sideQueriesData?.top_competitors || [];
        if (!Array.isArray(rows) || rows.length === 0) return [];
        return rows
            .map((r: any, index: number) => ({
                domain: r.domain_name,
                paidPresenceRate: Number(r.paid_presence_rate ?? 0),
                color: index === 0 ? '#4caf50' : undefined,
            }))
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 20);
    }, [sideQueriesData]);


    return {
        plaItems,
        textAds,
        domainItems,
        rightPanelItems,
        // Optimisé pour l'exécution en parallèle : le chargement est terminé quand au moins les side queries sont prêtes
        // car elles sont nécessaires pour la sélection du domaine et l'affichage de la liste de gauche
        isLoading: isFetchingSide || (isFetchingPLA && isFetchingTxt && isFetchingRightPanel),
    };
};
